import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useParams, useLocation } from "wouter"
import { ArrowRight, ArrowLeft, Target, AlertTriangle, TrendingUp } from "lucide-react"
import { useValueAssessment, SaveSummarySectionOptions } from "@/contexts/ValueAssessmentContext"
import { useState, useEffect, useRef, useCallback } from "react"
import PillSelector from "@/components/ui/PillSelector"
import { useUnsavedChangesWarning } from "@/hooks/useUnsavedChangesWarning"

interface ToolData {
  name: string
  licenseCost?: number
  serviceCost?: number
  knownIssues?: string
}

interface StrategicContextData {
  painPoints: string[]
  blockers: string[]
  tools: ToolData[]
  currency: string
  otherPainPoints?: string
  otherBlockers?: string
}

export default function StrategicContextScreen() {
  const params = useParams()
  const [_location, navigate] = useLocation()
  
  // Get projectId from URL params (traditional approach)
  const projectId = params.projectId
  const { data, updateSection, saveSummarySection } = useValueAssessment()
  // Public token support
  const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const publicToken = (qs?.get('t') || (typeof window !== 'undefined' ? sessionStorage.getItem('public_token') || '' : '')) as string
  const isPublic = Boolean(qs?.get('public') === 'true' || publicToken)
  
  // Predefined options based on specifications
  const painPointOptions = [
    "Manual journal entry prep",
    "Delay in intercompany matching",
    "High post-close adjustments",
    "Late reconciliations",
    "Lack of orchestration",
    "Approvals via email",
    "Lack of real-time visibility",
    "Workload spikes at month-end",
    "No ownership or accountability"
  ]
  
  const blockerOptions = [
    "Budget constraints",
    "Technical limitations",
    "Resistance to change",
    "Lack of expertise",
    "Regulatory requirements",
    "System compatibility",
    "Time constraints",
    "Management support"
  ]
  
  const toolOptions = [
    "BlackLine",
    "Trintech", 
    "Excel",
    "SAP",
    "RPA",
    "Other"
  ]

  
  // State management with proper hydration
  const [formData, setFormData] = useState<StrategicContextData>({
    painPoints: [],
    blockers: [],
    tools: [],
    currency: "USD",
    otherPainPoints: "",
    otherBlockers: ""
  })
  
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // ENHANCED: Function to persist Strategic Context data to valueSummary.strategicContext (v2.1 format)
  const persistStrategicContext = useCallback(async (contextData: StrategicContextData, options?: SaveSummarySectionOptions) => {
    if (!projectId) return;

    const strategicContextPayload = {
      painPoints: contextData.painPoints || [],
      implementationBlockers: contextData.blockers || [],
      currentTools: contextData.tools || [],
      strategicReadiness: contextData.currency || "USD",
      otherPainPoints: contextData.otherPainPoints || "",
      otherBlockers: contextData.otherBlockers || "",
      transformationDrivers: contextData.painPoints || [],
      strategicGoals: contextData.painPoints?.slice(0, 3) || [],
      totalToolsCount: contextData.tools.length,
      hasCustomPainPoints: !!(contextData.otherPainPoints?.trim()),
      hasCustomBlockers: !!(contextData.otherBlockers?.trim()),
      assessmentDate: new Date().toISOString().split('T')[0]
    };

    try {
      await saveSummarySection({
        strategicContext: strategicContextPayload,
        valueSummary: {
          strategicContext: strategicContextPayload
        },
        lastUpdatedAt: new Date().toISOString(),
        schemaVersion: 'v2.1'
      }, options);

      updateSection?.('context', {
        painPoints: contextData.painPoints || [],
        blockers: contextData.blockers || [],
        tools: contextData.tools || [],
        currency: contextData.currency || 'USD',
        otherPainPoints: contextData.otherPainPoints || '',
        otherBlockers: contextData.otherBlockers || ''
      }, { autoSave: false });

      setHasLocalChanges(false);
    } catch (error) {
      console.error('Error updating valueSummary strategicContext:', error);
      throw error;
    }
  }, [projectId, saveSummarySection, updateSection])

  const persistStrategicContextWithCurrent = useCallback((options?: SaveSummarySectionOptions) => {
    return persistStrategicContext(formData, options)
  }, [formData, persistStrategicContext])
  
  // Reactive hydration that responds to data changes from ValueAssessmentContext
  const hasHydratedRef = useRef(false)
  useEffect(() => {
    // Hydrate from either legacy context or v2.1 summary_data.strategicContext
    const anyData: any = data || {}
    const ctx = (anyData.context || {}) as any
    const serverStrategic = anyData.summary_data?.strategicContext || {}

    const rawTools = ctx.tools || ctx.currentTools || serverStrategic.currentTools || []
    const cleanTools = (rawTools as any[]).map((tool: any) => {
      if (tool && typeof tool === 'object' && !tool.name && Object.keys(tool).some(k => !isNaN(Number(k as any)))) {
        const chars = Object.keys(tool).filter(k => !isNaN(Number(k as any))).sort((a, b) => Number(a) - Number(b))
        const reconstructedName = chars.map(k => (tool as any)[k]).join('')
        return {
          name: reconstructedName,
          licenseCost: tool.licenseCost || 100000,
          serviceCost: tool.serviceCost || 50000,
          knownIssues: tool.knownIssues || ""
        }
      }
      if (typeof tool === 'string') {
        return { name: tool, licenseCost: 100000, serviceCost: 50000, knownIssues: "" }
      }
      return tool && typeof tool === 'object' && tool.name ? tool : { name: String(tool), licenseCost: 100000, serviceCost: 50000, knownIssues: "" }
    }).filter((tool: ToolData) => tool.name && tool.name.trim())

    const hydratedData: StrategicContextData = {
      painPoints: ctx.painPoints || serverStrategic.painPoints || [],
      blockers: ctx.blockers || ctx.implementationBlockers || serverStrategic.implementationBlockers || [],
      tools: cleanTools,
      currency: ctx.currency || ctx.strategicReadiness || serverStrategic.strategicReadiness || 'USD',
      otherPainPoints: (ctx.otherPainPoints ?? serverStrategic.otherPainPoints) ?? '',
      otherBlockers: (ctx.otherBlockers ?? serverStrategic.otherBlockers) ?? ''
    }

    // Determine if incoming is empty and if current form has selections
    const incomingIsEmpty =
      (hydratedData.painPoints?.length ?? 0) === 0 &&
      (hydratedData.blockers?.length ?? 0) === 0 &&
      (hydratedData.tools?.length ?? 0) === 0 &&
      !(hydratedData.otherPainPoints && hydratedData.otherPainPoints.trim()) &&
      !(hydratedData.otherBlockers && hydratedData.otherBlockers.trim())

    const currentHasSelections =
      (formData.painPoints?.length ?? 0) > 0 ||
      (formData.blockers?.length ?? 0) > 0 ||
      (formData.tools?.length ?? 0) > 0 ||
      !!(formData.otherPainPoints && formData.otherPainPoints.trim()) ||
      !!(formData.otherBlockers && formData.otherBlockers.trim())

    // Only hydrate on first load, or when no local changes AND
    // either incoming data is not empty or our current form is empty.
    if (!hasHydratedRef.current || (!hasLocalChanges && (!incomingIsEmpty || !currentHasSelections))) {
      setFormData(hydratedData)
      // Do NOT clear hasLocalChanges here; only clear after successful save
      hasHydratedRef.current = true
    }

    // If we fixed corrupted tools, immediately save the clean data
    if (cleanTools.length > 0 && (ctx || serverStrategic)) {
      const hasNewFixed = cleanTools.some(tool => tool.name && !rawTools?.some((t: any) => (typeof t === 'string' ? t : t?.name) === tool.name))
      if (hasNewFixed) {
        setTimeout(() => {
          persistStrategicContext(hydratedData).catch(error => {
            console.error('[HYDRATION] Failed to persist cleaned strategic context:', error)
          })
        }, 500)
      }
    }

    // Ensure tools render; safe no-op re-render
    if (hydratedData.tools.length > 0) {
      setTimeout(() => setFormData(prev => ({ ...prev })), 100)
    }
  }, [data.context, (data as any)?.summary_data?.strategicContext, hasLocalChanges, persistStrategicContext])

  // Public-mode hydration directly from token endpoint
  useEffect(() => {
    if (!isPublic || !publicToken) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/value-assessment/${publicToken}`)
        if (!res.ok) return;
        const json = await res.json()
        const sc = (json && (json.strategicContext || (json.data && json.data.summary_data && json.data.summary_data.strategicContext))) || {}
        const hydrated: StrategicContextData = {
          painPoints: sc.painPoints || [],
          blockers: sc.implementationBlockers || [],
          tools: Array.isArray(sc.currentTools) ? sc.currentTools.map((t: any) => typeof t === 'string' ? { name: t } : t) : [],
          currency: sc.strategicReadiness || 'USD',
          otherPainPoints: sc.otherPainPoints || '',
          otherBlockers: sc.otherBlockers || ''
        }
        setFormData(hydrated)
      } catch (e) {
        console.error('[PUBLIC LOAD] Failed to hydrate strategicContext via token', e)
      }
    })()
  }, [isPublic, publicToken])

  // Handlers
  const handleContinue = async () => {
    let didPersist = false;
    try {
      if (hasLocalChanges) {
        await persistStrategicContextWithCurrent()
        didPersist = true
      }
    } catch (error) {
      console.error('❌ [SAVE] Failed to persist Strategic Context data:', error)
      return
    }
    
    // Final comprehensive logging for valueSummary.strategicContext blob
    console.log('✅ [FINAL SAVE] StrategicContext complete blob structure:', {
      blobFieldsCount: Object.keys(formData).length + 6, // +6 for calculated fields
      painPoints: formData.painPoints,
      implementationBlockers: formData.blockers,
      currentTools: formData.tools.map(t => t.name || t),
      strategicReadiness: formData.currency,
      otherPainPoints: formData.otherPainPoints || "(none)",
      otherBlockers: formData.otherBlockers || "(none)",
      totalDataCaptured: {
        painPointsCount: formData.painPoints.length,
        blockersCount: formData.blockers.length,
        toolsCount: formData.tools.length,
        hasCustomInputs: !!(formData.otherPainPoints?.trim() || formData.otherBlockers?.trim())
      }
    })
    
    if (didPersist) {
      console.log("✅ Rewired: Strategic Context data persisted to valueSummary.strategicContext v2.1 format")
    }
    
    // Preserve public parameter if present
    const urlParams = new URLSearchParams(window.location.search)
    const isPublicFlow = urlParams.get('public') === 'true' || urlParams.has('t')
    const t = urlParams.get('t')
    const nextUrl = isPublicFlow 
      ? `/value-assessment/${projectId}/allocation?public=true${t ? `&t=${t}` : ''}`
      : `/value-assessment/${projectId}/allocation`
    
    navigate(nextUrl)
  }

  const handleBack = async () => {
    if (hasLocalChanges) {
      try {
        await persistStrategicContextWithCurrent()
      } catch (error) {
        console.error('❌ [SAVE] Failed to persist Strategic Context data before navigating back:', error)
        return
      }
    }

    // Preserve public parameter if present
    const urlParams = new URLSearchParams(window.location.search)
    const isPublicFlow = urlParams.get('public') === 'true' || urlParams.has('t')
    const t = urlParams.get('t')
    const backUrl = isPublicFlow 
      ? `/value-assessment/${projectId}/kickoff?public=true${t ? `&t=${t}` : ''}`
      : `/value-assessment/${projectId}/kickoff`
    
    navigate(backUrl)
  }

  const updateFormData = (field: keyof StrategicContextData, value: any) => {
    setHasLocalChanges(true)
    const updatedData = { ...formData, [field]: value }
    setFormData(updatedData)    
  }

  const handleToolSelect = (selectedTools: string[]) => {
    console.log('[TOOL SELECT] Incoming tools:', selectedTools)
    
    const updatedTools = selectedTools.map(toolName => {
      // Find existing tool, handling corrupted data
      const existingTool = formData.tools.find((t: ToolData) => {
        if (t && typeof t === 'object' && t.name) return t.name === toolName
        if (t && typeof t === 'object' && !t.name && Object.keys(t).some(k => !isNaN(Number(k)))) {
          // Reconstruct name from corrupted data
          const chars = Object.keys(t).filter(k => !isNaN(Number(k))).sort((a, b) => Number(a) - Number(b))
          const reconstructedName = chars.map(k => (t as any)[k]).join('')
          return reconstructedName === toolName
        }
        return false
      })
      
      return existingTool && existingTool.name 
        ? existingTool 
        : { name: toolName, licenseCost: 0, serviceCost: 0, knownIssues: "" }
    })
    
    console.log('[TOOL SELECT] Updated tools:', updatedTools)
    updateFormData('tools', updatedTools)
  }

  const updateToolCost = (toolName: string, field: 'licenseCost' | 'serviceCost' | 'knownIssues', value: number | string) => {
    const updatedTools = formData.tools.map(tool => 
      tool.name === toolName 
        ? { ...tool, [field]: value }
        : tool
    )
    updateFormData('tools', updatedTools)
  }

  useUnsavedChangesWarning(hasLocalChanges, (options) => {
    persistStrategicContextWithCurrent(options).catch(() => {})
  })

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return ''
    if (value === 0) return '0'
    return value.toLocaleString()
  }

  const parseCurrency = (value: string): number => {
    if (!value || value.trim() === '') return 0
    return parseInt(value.replace(/[,$]/g, '')) || 0
  }

  // Fix corrupted character-indexed tool objects
  const selectedToolNames = formData.tools.map((tool: ToolData) => {
    // Handle corrupted character-indexed objects
    if (tool && typeof tool === 'object' && !tool.name && Object.keys(tool).some(k => !isNaN(Number(k)))) {
      // Reconstruct name from character indices
      const chars = Object.keys(tool).filter(k => !isNaN(Number(k))).sort((a, b) => Number(a) - Number(b))
      const reconstructedName = chars.map(k => (tool as any)[k]).join('')
      console.log(`🔧 Fixed corrupted tool:`, { reconstructed: reconstructedName })
      return reconstructedName
    }
    return tool.name || String(tool)
  }).filter(Boolean) as string[]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Strategic Context</h1>
          <p className="text-gray-300 mt-2">Define transformation priorities and value drivers</p>
        </div>
        <div className="text-sm text-gray-400">
          Step 2 of 6
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pain Points Section */}
        <Card className="bg-forestBlack border-[#334155]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#4AE2C9]">
              <Target className="w-5 h-5" />
              Current R2R Challenges
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <PillSelector
                options={painPointOptions}
                selected={formData.painPoints}
                onChange={(selected) => {
                  console.log('[UI DEBUG] PillSelector onChange - painPoints:', selected)
                  updateFormData('painPoints', selected)
                }}
                allowCustom={true}
                data-cy="pain-points"
            />

            {/* Debug: Current pain points state */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-800 rounded">
                Debug: {formData.painPoints.length} pain points: [{formData.painPoints.join(', ')}]
              </div>
            )}
            
            {/* Optional other pain points */}
            <div className="mt-4">
              <Label className="text-gray-300 text-sm">Other pain points (optional)</Label>
              <Textarea
                value={formData.otherPainPoints || ''}
                onChange={(e) => updateFormData('otherPainPoints', e.target.value)}
                placeholder="Describe any additional pain points not covered above..."
                className="bg-redwoodInputFieldBg border-[#334155] text-white mt-2"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Blockers Section */}
        <Card className="bg-forestBlack border-[#334155]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#6B9FED]">
              <AlertTriangle className="w-5 h-5" />
              Blockers to R2R Improvement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <PillSelector
              options={blockerOptions}
              selected={formData.blockers}
              onChange={(selected) => {
                console.log('[UI DEBUG] PillSelector onChange - blockers:', selected)
                updateFormData('blockers', selected)
              }}
              allowCustom={true}
              data-cy="blockers"
            />
            {/* Debug: Current blockers state */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-800 rounded">
                Debug: {formData.blockers.length} blockers: [{formData.blockers.join(', ')}]
              </div>
            )}
            
            {/* Optional other blockers */}
            <div className="mt-4">
              <Label className="text-gray-300 text-sm">Other blockers (optional)</Label>
              <Textarea
                value={formData.otherBlockers || ''}
                onChange={(e) => updateFormData('otherBlockers', e.target.value)}
                placeholder="Describe any additional blockers not covered above..."
                className="bg-redwoodInputFieldBg border-[#334155] text-white mt-2"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tools Section */}
      <Card className="bg-forestBlack border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-[#F59E0B]">
            <TrendingUp className="w-5 h-5" />
            Tools Currently in Use for R2R
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tool Selection */}
          <div className="mb-4">
            <Label className="text-gray-300 text-sm mb-2 block">Select tools and systems you currently use:</Label>
            <PillSelector
              options={toolOptions}
              selected={selectedToolNames}
              onChange={(selected) => {
                console.log('[UI DEBUG] PillSelector onChange - tools:', selected)
                handleToolSelect(selected)
              }}
              allowCustom={true}
              data-cy="tools"
            />
            {/* Debug: Current tools state */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-800 rounded">
                Debug: {formData.tools.length} tools: [{selectedToolNames.join(', ')}]
              </div>
            )}
          </div>

          {/* Tool Cost Details - Always show if tools exist, prevent flashing */}
          {formData.tools.length > 0 && (
            <div className="space-y-4" key={`tools-${formData.tools.length}`}>
              <Label className="text-gray-300 text-sm">Cost details for selected tools:</Label>
              {formData.tools.map((tool, index) => (
                <div key={tool.name} className="bg-redwoodInputFieldBg p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300 text-sm font-medium" data-cy={tool.name}>{tool.name}</Label>
                      <div className="text-xs text-gray-400 mt-1">Tool/System</div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-300 text-sm">Annual License Cost</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          value={formatCurrency(tool.licenseCost)}
                          onChange={(e) => updateToolCost(tool.name, 'licenseCost', parseCurrency(e.target.value))}
                          onFocus={(e) => {
                            if (e.target.value === '0' || e.target.value === '') {
                              e.target.value = ''
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') {
                              updateToolCost(tool.name, 'licenseCost', 0)
                            }
                          }}
                          placeholder="Enter amount"
                          className="bg-forestBlack border-[#334155] text-white text-right pr-12"
                          data-cy={`licenseCost-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                          $
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-300 text-sm">Annual Service Cost</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          value={formatCurrency(tool.serviceCost)}
                          onChange={(e) => updateToolCost(tool.name, 'serviceCost', parseCurrency(e.target.value))}
                          onFocus={(e) => {
                            if (e.target.value === '0' || e.target.value === '') {
                              e.target.value = ''
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') {
                              updateToolCost(tool.name, 'serviceCost', 0)
                            }
                          }}
                          placeholder="Enter amount"
                          className="bg-forestBlack border-[#334155] text-white text-right pr-12"
                          data-cy={`serviceCost-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}

                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                          $
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Known Issues */}
                  <div className="mt-3">
                    <Label className="text-gray-300 text-sm">Known Issues (optional)</Label>
                    <Input
                      value={tool.knownIssues || ''}
                      onChange={(e) => updateToolCost(tool.name, 'knownIssues', e.target.value)}
                      placeholder="e.g., Limited automation in journal entries"
                      className="bg-forestBlack border-[#334155] text-white mt-1"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button 
          onClick={handleBack}
          variant="outline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={handleContinue}
          className="bg-primary text-forestBlack hover:bg-primaryHover"
          data-cy="configurationButton"
        >
          Continue to Configuration
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
